import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MapPin, Plus, Pencil, Trash2, Building2, Waves, Save, X, Globe, Mail, Phone, ChevronDown, ChevronUp } from "lucide-react";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

// ===== MODAL COMPONENT =====
const EditModal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
      <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full">
          <X className="w-5 h-5 text-gray-500" />
        </button>
      </div>
      <div className="p-6">{children}</div>
    </div>
  </div>
);

const LocationsManagement = () => {
  const [locations, setLocations] = useState([]);
  const [diveCenters, setDiveCenters] = useState([]);
  const [diveSites, setDiveSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedLocations, setExpandedLocations] = useState({});

  // Modal states
  const [editModal, setEditModal] = useState(null); // {type: 'location'|'center'|'site', data: {...}, isNew: bool}

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [locsRes, centersRes, sitesRes] = await Promise.all([
        axios.get(`${API}/locations`),
        axios.get(`${API}/map/dive-centers`),
        axios.get(`${API}/map/dive-sites`)
      ]);
      setLocations(locsRes.data || []);
      setDiveCenters(centersRes.data || []);
      setDiveSites(sitesRes.data || []);
    } catch (err) {
      console.error("Failed to load data:", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (locId) => {
    setExpandedLocations(prev => ({ ...prev, [locId]: !prev[locId] }));
  };

  // ===== LOCATION CRUD =====
  const openNewLocation = () => {
    setEditModal({
      type: 'location', isNew: true,
      data: { name: "", country: "", description: "", coordinates: { lat: 0, lng: 0 }, image: "" }
    });
  };

  const openEditLocation = (loc) => {
    setEditModal({
      type: 'location', isNew: false, id: loc.id,
      data: { name: loc.name, country: loc.country, description: loc.description || "", coordinates: loc.coordinates || { lat: 0, lng: 0 }, image: loc.image || "" }
    });
  };

  const saveLocation = async (formData) => {
    try {
      if (editModal.isNew) {
        await axios.post(`${API}/locations`, formData, { headers });
      } else {
        await axios.put(`${API}/locations/${editModal.id}`, formData, { headers });
      }
      setEditModal(null);
      fetchAll();
    } catch (err) {
      console.error("Failed to save location:", err);
    }
  };

  const deleteLocation = async (id) => {
    if (!window.confirm("Delete this location? Dive centers and sites will be unlinked.")) return;
    try {
      await axios.delete(`${API}/locations/${id}`, { headers });
      fetchAll();
    } catch (err) {
      console.error("Failed to delete:", err);
    }
  };

  // ===== DIVE CENTER CRUD =====
  const openEditCenter = (center) => {
    setEditModal({
      type: 'center', isNew: false, id: center.id,
      data: {
        name: center.name || "", location: center.location || "", country: center.country || "",
        location_id: center.location_id || "", short_description: center.short_description || "",
        full_description: center.full_description || "", image: center.image || "",
        website: center.website || "", email: center.email || "", phone: center.phone || "",
        amenities: (center.amenities || []).join(", "),
        coordinates: center.coordinates || { lat: 0, lng: 0 }
      }
    });
  };

  const saveCenter = async (formData) => {
    try {
      const payload = {
        ...formData,
        amenities: formData.amenities ? formData.amenities.split(',').map(a => a.trim()).filter(Boolean) : [],
        location_id: formData.location_id || null
      };
      await axios.put(`${API}/map/dive-centers/${editModal.id}`, payload, { headers });
      setEditModal(null);
      fetchAll();
    } catch (err) {
      console.error("Failed to save center:", err);
    }
  };

  // ===== ASSIGN HELPERS =====
  const handleAssignCenter = async (centerId, locationId) => {
    try {
      await axios.put(`${API}/map/dive-centers/${centerId}`, { location_id: locationId || null }, { headers });
      fetchAll();
    } catch (err) {
      console.error("Failed to assign center:", err);
    }
  };

  const handleAssignSite = async (siteId, locationId) => {
    try {
      await axios.put(`${API}/map/dive-sites/${siteId}`, { location_id: locationId || null }, { headers });
      fetchAll();
    } catch (err) {
      console.error("Failed to assign site:", err);
    }
  };

  const getCentersForLocation = (locId) => diveCenters.filter(c => c.location_id === locId);
  const getSitesForLocation = (locId) => diveSites.filter(s => s.location_id === locId);
  const unassignedCenters = diveCenters.filter(c => !c.location_id);
  const unassignedSites = diveSites.filter(s => !s.location_id && !s.is_standalone);

  if (loading) return <div className="text-center py-8 text-gray-500">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="locations-management">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Location Hierarchy</h2>
          <p className="text-gray-500 text-sm mt-1">Locations are the top level. Dive centers and dive sites belong to locations.</p>
        </div>
        <Button onClick={openNewLocation} data-testid="add-location-btn">
          <Plus className="w-4 h-4 mr-2" /> New Location
        </Button>
      </div>

      {/* Location Cards */}
      {locations.map(loc => {
        const centers = getCentersForLocation(loc.id);
        const sites = getSitesForLocation(loc.id);
        const isExpanded = expandedLocations[loc.id] !== false; // default expanded

        return (
          <Card key={loc.id} className="border border-gray-200" data-testid={`location-card-${loc.id}`}>
            <CardContent className="pt-5 pb-4">
              {/* Location Header */}
              <div className="flex items-center justify-between">
                <button className="flex items-center gap-3 text-left flex-1" onClick={() => toggleExpand(loc.id)}>
                  <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-cyan-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-gray-900">{loc.name}</h3>
                    <p className="text-sm text-gray-500">{loc.country} &middot; {centers.length} centers &middot; {sites.length} sites</p>
                  </div>
                  {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                </button>
                <div className="flex gap-1 ml-3">
                  <Button size="sm" variant="outline" onClick={() => openEditLocation(loc)} data-testid={`edit-location-${loc.id}`}>
                    <Pencil className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => deleteLocation(loc.id)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="mt-4 ml-2 space-y-4">
                  {loc.description && <p className="text-sm text-gray-600 italic">{loc.description}</p>}

                  {/* Dive Centers */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-blue-500" /> Dive Centers ({centers.length})
                    </h4>
                    {centers.length > 0 ? (
                      <div className="space-y-2">
                        {centers.map(c => (
                          <div key={c.id} className="flex items-center justify-between bg-blue-50/60 rounded-lg px-4 py-3 border border-blue-100">
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              {c.image && <img src={c.image.startsWith('http') ? c.image : `${BACKEND_URL}${c.image}`} alt="" className="w-8 h-8 rounded object-contain bg-white" />}
                              <div className="min-w-0">
                                <p className="font-medium text-gray-900 text-sm truncate">{c.name}</p>
                                <p className="text-xs text-gray-500 truncate">{c.short_description}</p>
                              </div>
                            </div>
                            <div className="flex gap-1 ml-2 flex-shrink-0">
                              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => openEditCenter(c)}>
                                <Pencil className="w-3 h-3" />
                              </Button>
                              <Button size="sm" variant="ghost" className="text-red-500 h-7 px-2" onClick={() => handleAssignCenter(c.id, null)}>
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 italic ml-6">No dive centers assigned</p>
                    )}
                  </div>

                  {/* Dive Sites */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <Waves className="w-4 h-4 text-teal-500" /> Dive Sites ({sites.length})
                    </h4>
                    {sites.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {sites.map(s => (
                          <span key={s.id} className="inline-flex items-center gap-1 bg-teal-50 border border-teal-100 rounded-full px-3 py-1 text-sm text-gray-700">
                            {s.name}
                            <button className="text-red-400 hover:text-red-600 ml-1" onClick={() => handleAssignSite(s.id, null)}>
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 italic ml-6">No dive sites assigned</p>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* Unassigned entities */}
      {(unassignedCenters.length > 0 || unassignedSites.length > 0) && (
        <Card className="border border-amber-300 bg-amber-50/50">
          <CardContent className="pt-5">
            <h3 className="text-base font-bold text-amber-800 mb-3">Unassigned</h3>
            
            {unassignedCenters.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Dive Centers</h4>
                {unassignedCenters.map(c => (
                  <div key={c.id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 text-sm mb-1 border">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{c.name}</span>
                      <span className="text-gray-400 text-xs">({c.location})</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <select className="border rounded px-2 py-1 text-xs" value="" onChange={(e) => { if (e.target.value) handleAssignCenter(c.id, e.target.value); }}>
                        <option value="">Assign to...</option>
                        {locations.map(l => <option key={l.id} value={l.id}>{l.name}, {l.country}</option>)}
                      </select>
                      <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => openEditCenter(c)}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {unassignedSites.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Dive Sites</h4>
                {unassignedSites.map(s => (
                  <div key={s.id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 text-sm mb-1 border">
                    <span className="font-medium">{s.name}</span>
                    <select className="border rounded px-2 py-1 text-xs" value="" onChange={(e) => { if (e.target.value) handleAssignSite(s.id, e.target.value); }}>
                      <option value="">Assign to...</option>
                      {locations.map(l => <option key={l.id} value={l.id}>{l.name}, {l.country}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {locations.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <MapPin className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>No locations yet. Create one to organize your dive centers and sites.</p>
        </div>
      )}

      {/* ===== EDIT MODALS ===== */}

      {/* Location Modal */}
      {editModal?.type === 'location' && (
        <LocationEditModal
          data={editModal.data}
          isNew={editModal.isNew}
          onSave={saveLocation}
          onClose={() => setEditModal(null)}
        />
      )}

      {/* Dive Center Modal */}
      {editModal?.type === 'center' && (
        <CenterEditModal
          data={editModal.data}
          locations={locations}
          onSave={saveCenter}
          onClose={() => setEditModal(null)}
        />
      )}
    </div>
  );
};

// ===== LOCATION EDIT MODAL =====
const LocationEditModal = ({ data, isNew, onSave, onClose }) => {
  const [form, setForm] = useState({ ...data });

  return (
    <EditModal title={isNew ? "New Location" : "Edit Location"} onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="space-y-4">
        <div>
          <Label>Name *</Label>
          <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Panglao, Bohol" required data-testid="location-name-input" />
        </div>
        <div>
          <Label>Country *</Label>
          <Input value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} placeholder="e.g. Philippines" required data-testid="location-country-input" />
        </div>
        <div>
          <Label>Description</Label>
          <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Brief description" rows={3} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Latitude</Label>
            <Input type="number" step="any" value={form.coordinates?.lat || 0} onChange={e => setForm({ ...form, coordinates: { ...form.coordinates, lat: parseFloat(e.target.value) || 0 } })} />
          </div>
          <div>
            <Label>Longitude</Label>
            <Input type="number" step="any" value={form.coordinates?.lng || 0} onChange={e => setForm({ ...form, coordinates: { ...form.coordinates, lng: parseFloat(e.target.value) || 0 } })} />
          </div>
        </div>
        <div>
          <Label>Image URL</Label>
          <Input value={form.image} onChange={e => setForm({ ...form, image: e.target.value })} placeholder="https://..." />
        </div>
        <div className="flex gap-2 pt-2">
          <Button type="submit" className="flex-1" data-testid="save-location-btn"><Save className="w-4 h-4 mr-2" /> Save</Button>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        </div>
      </form>
    </EditModal>
  );
};

// ===== DIVE CENTER EDIT MODAL =====
const CenterEditModal = ({ data, locations, onSave, onClose }) => {
  const [form, setForm] = useState({ ...data });
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem("token");
      const response = await fetch(`${BACKEND_URL}/api/upload/dive-center-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const result = await response.json();
      setForm(prev => ({ ...prev, image: result.image_url }));
    } catch (error) {
      console.error("Upload failed:", error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <EditModal title="Edit Dive Center" onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="space-y-4">
        <div>
          <Label>Name *</Label>
          <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
        </div>
        <div>
          <Label>Parent Location</Label>
          <select className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" value={form.location_id || ""} onChange={e => setForm({ ...form, location_id: e.target.value || "" })}>
            <option value="">Unassigned</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.name}, {l.country}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Location Label</Label>
            <Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="e.g. Panglao, Bohol" />
          </div>
          <div>
            <Label>Country</Label>
            <Input value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Latitude</Label>
            <Input type="number" step="any" value={form.coordinates?.lat || 0} onChange={e => setForm({ ...form, coordinates: { ...form.coordinates, lat: parseFloat(e.target.value) || 0 } })} />
          </div>
          <div>
            <Label>Longitude</Label>
            <Input type="number" step="any" value={form.coordinates?.lng || 0} onChange={e => setForm({ ...form, coordinates: { ...form.coordinates, lng: parseFloat(e.target.value) || 0 } })} />
          </div>
        </div>
        <div>
          <Label>Short Description *</Label>
          <Textarea value={form.short_description} onChange={e => setForm({ ...form, short_description: e.target.value })} rows={2} required />
        </div>
        <div>
          <Label>Full Description</Label>
          <Textarea value={form.full_description} onChange={e => setForm({ ...form, full_description: e.target.value })} rows={4} />
        </div>
        <div>
          <Label>Logo / Image</Label>
          <Input type="file" accept="image/*" onChange={handleImageUpload} disabled={uploading} />
          {form.image && (
            <div className="mt-2 flex items-center gap-2">
              <img src={form.image.startsWith('http') ? form.image : `${BACKEND_URL}${form.image}`} alt="Preview" className="h-12 w-auto rounded border" />
              <button type="button" className="text-xs text-red-500 hover:underline" onClick={() => setForm({ ...form, image: "" })}>Remove</button>
            </div>
          )}
          {uploading && <p className="text-xs text-gray-500 mt-1">Uploading...</p>}
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="flex items-center gap-1"><Globe className="w-3 h-3" /> Website</Label>
            <Input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} placeholder="https://..." />
          </div>
          <div>
            <Label className="flex items-center gap-1"><Mail className="w-3 h-3" /> Email</Label>
            <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} type="email" />
          </div>
          <div>
            <Label className="flex items-center gap-1"><Phone className="w-3 h-3" /> Phone</Label>
            <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
          </div>
        </div>
        <div>
          <Label>Amenities (comma-separated)</Label>
          <Input value={form.amenities} onChange={e => setForm({ ...form, amenities: e.target.value })} placeholder="PADI courses, Equipment rental, etc." />
        </div>
        <div className="flex gap-2 pt-2">
          <Button type="submit" className="flex-1"><Save className="w-4 h-4 mr-2" /> Save Changes</Button>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        </div>
      </form>
    </EditModal>
  );
};

export default LocationsManagement;
